import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { inputCls } from "../hooks/useMasterMaps";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, TrendingDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { StarPicker } from "../components/PipelineBits";

export default function Onboarding() {
  const { can } = useAuth();
  const [data, setData] = useState({ rows: [], pendency: [] });
  const [probation, setProbation] = useState([]);
  const [attrition, setAttrition] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [revDlg, setRevDlg] = useState(null);
  const [rev, setRev] = useState({ rating: 0, outcome: "Confirm", comments: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/onboarding").then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
    api.get("/probation").then(({ data }) => setProbation(data)).catch(() => {});
    api.get("/analytics/early-attrition").then(({ data }) => setAttrition(data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const canWrite = can("onboarding", "write");

  const setItem = async (onb, item, status) => {
    try {
      await api.post(`/onboarding/${onb.onb_id}/item`, { item: item.item, status });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const completeReview = async () => {
    if (!rev.rating) { toast.error("Rating is required"); return; }
    setBusy(true);
    try {
      await api.post(`/probation/${revDlg.review_id}/complete`, rev);
      toast.success(`${revDlg.review_type} review — ${rev.outcome}${rev.outcome === "Exit" ? " (early attrition tagged)" : ""}`);
      setRevDlg(null); setRev({ rating: 0, outcome: "Confirm", comments: "" }); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const dueReviews = probation.filter((r) => !r.completed_on);

  return (
    <div data-testid="onboarding-page">
      <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Onboarding & Probation</h1>
      <p className="text-sm text-[#6B7280] mt-1 mb-6">Checklist pendency by owner, 30/60/90-day reviews, and early attrition tagged to source & panel.</p>

      {data.pendency.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="pendency-chips">
          {data.pendency.map((p) => (
            <span key={p.owner} className="inline-flex items-center gap-2 border border-[#D97706]/40 bg-[#D97706]/5 text-xs px-3 py-1.5 rounded-sm">
              <span className="text-[#0E2240] font-medium">{p.owner}</span>
              <span className="text-[#D97706] font-semibold tabular-nums">{p.pending} pending</span>
            </span>
          ))}
        </div>
      )}

      <h2 className="font-serif text-lg text-[#0E2240] mb-3">Joiners ({data.rows.length})</h2>
      <div className="space-y-3 mb-10" data-testid="onboarding-list">
        {data.rows.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-sm p-6 text-sm text-[#6B7280]">No onboarding in progress.</div>
        ) : data.rows.map((r) => (
          <div key={r.onb_id} className="bg-white border border-gray-200 rounded-sm">
            <button data-testid={`onb-row-${r.onb_id}`} onClick={() => setExpanded(expanded === r.onb_id ? null : r.onb_id)}
              className="w-full flex items-center justify-between p-4 text-left">
              <div>
                <span className="text-sm font-medium text-[#0E2240]">{r.employee_name}</span>
                <span className="ml-2 text-xs text-[#6B7280] tabular-nums">{r.employee_code}</span>
                <div className="text-xs text-[#6B7280] mt-0.5">{r.desig_name} · {r.branch_name} · joined {r.date_of_joining}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 hidden sm:block">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#1B7F3B]" style={{ width: `${(r.progress.done / r.progress.total) * 100}%` }} />
                  </div>
                </div>
                <span className="text-xs tabular-nums text-[#6B7280]">{r.progress.done}/{r.progress.total}</span>
                {expanded === r.onb_id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </div>
            </button>
            {expanded === r.onb_id && (
              <div className="border-t border-gray-100 p-4 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`onb-items-${r.onb_id}`}>
                {r.items.map((i) => (
                  <div key={i.item} className="flex items-center justify-between border border-gray-100 rounded-sm px-3 py-2 text-xs">
                    <div>
                      <span className={i.status === "Done" ? "text-[#1B7F3B] line-through" : "text-[#0E2240]"}>{i.item}</span>
                      <div className="text-[10px] text-[#6B7280]">Owner: {i.owner_name}</div>
                    </div>
                    {canWrite ? (
                      <input type="checkbox" data-testid={`onb-item-${r.onb_id}-${i.item.replace(/\s/g, "-")}`}
                        checked={i.status === "Done"} onChange={(e) => setItem(r, i, e.target.checked ? "Done" : "Pending")} />
                    ) : (
                      <span className={`text-[10px] uppercase ${i.status === "Done" ? "text-[#1B7F3B]" : "text-[#D97706]"}`}>{i.status}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <h2 className="font-serif text-lg text-[#0E2240] mb-3">Probation reviews due ({dueReviews.length})</h2>
      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto mb-10">
        <table className="w-full text-sm" data-testid="probation-table">
          <thead>
            <tr className="border-b border-gray-200 bg-[#F5F6F8]">
              {["Employee", "Review", "Due", "Reviewer", "Status", ""].map((h, i) => (
                <th key={i} className="px-3 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {probation.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[#6B7280]">No reviews scheduled</td></tr>
            ) : probation.map((r) => (
              <tr key={r.review_id} data-testid={`review-row-${r.review_id}`} className="border-b border-gray-100 last:border-0">
                <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                  <div className="text-[#0E2240] font-medium">{r.employee_name}</div>
                  <div className="text-[10px] text-[#6B7280]">{r.employee_code} · {r.desig_name} · {r.branch_name}</div>
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs">{r.review_type}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums">
                  <span className={r.overdue ? "text-[#BB162B] font-semibold" : ""}>{r.due_date}</span>
                  {r.overdue && <span className="ml-1 text-[9px] uppercase text-[#BB162B]">overdue</span>}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs">{r.reviewer_name}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                  {r.completed_on ? (
                    <span className={`font-medium ${r.outcome === "Exit" ? "text-[#BB162B]" : r.outcome === "Extend" ? "text-[#D97706]" : "text-[#1B7F3B]"}`}>
                      {r.outcome} ({r.rating}★)
                    </span>
                  ) : <span className="text-[#D97706]">Pending</span>}
                </td>
                <td className="px-3 py-2.5 text-right">
                  {!r.completed_on && canWrite && (
                    <button data-testid={`review-complete-${r.review_id}`} onClick={() => setRevDlg(r)}
                      className="text-xs text-[#1E5AA8] hover:underline">Complete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-serif text-lg text-[#0E2240] mb-3 flex items-center gap-2"><TrendingDown size={17} className="text-[#BB162B]" /> Early attrition (exits within 90 days)</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="attrition-panel">
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-3">By source of hire</div>
          {!attrition || attrition.by_source.length === 0 ? (
            <div className="text-xs text-[#6B7280]">No early attrition recorded.</div>
          ) : attrition.by_source.map((s) => (
            <div key={s.source} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
              <span className="text-[#0E2240]">{s.source}</span>
              <span className="text-[#BB162B] font-semibold tabular-nums">{s.count}</span>
            </div>
          ))}
          {attrition?.by_panel?.length > 0 && (
            <>
              <div className="text-xs uppercase tracking-wider text-[#6B7280] mt-4 mb-2">By interview panel</div>
              {attrition.by_panel.map((p) => (
                <div key={p.panelist} className="flex items-center justify-between text-xs py-1">
                  <span className="text-[#0E2240]">{p.panelist}</span>
                  <span className="text-[#D97706] font-semibold tabular-nums">{p.count}</span>
                </div>
              ))}
            </>
          )}
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-3">Recent cases</div>
          {!attrition || attrition.rows.length === 0 ? (
            <div className="text-xs text-[#6B7280]">Nothing to report — a good sign.</div>
          ) : attrition.rows.slice(0, 8).map((r, i) => (
            <div key={i} className="text-xs py-1.5 border-b border-gray-50 last:border-0">
              <span className="text-[#0E2240] font-medium">{r.full_name}</span>
              <span className="text-[#6B7280]"> ({r.employee_code}) · {r.branch_name} · exited after </span>
              <span className="text-[#BB162B] font-semibold tabular-nums">{r.days_to_exit}d</span>
              <span className="text-[#6B7280]"> · {r.source}</span>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!revDlg} onOpenChange={(o) => !o && setRevDlg(null)}>
        {revDlg && (
          <DialogContent className="bg-white rounded-sm shadow-none border border-gray-200 max-w-md" data-testid="review-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-[#0E2240]">{revDlg.review_type} review — {revDlg.employee_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-1">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#0E2240]">Performance rating</span>
                <StarPicker value={rev.rating} onChange={(v) => setRev((r) => ({ ...r, rating: v }))} size={24} testid="review-rating" />
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="review-outcomes">
                {["Confirm", "Extend", "Exit"].map((o) => (
                  <button key={o} data-testid={`review-outcome-${o}`} onClick={() => setRev((r) => ({ ...r, outcome: o }))}
                    className={`py-2 text-sm rounded-sm border transition-colors duration-200 ${rev.outcome === o
                      ? o === "Exit" ? "bg-[#BB162B] text-white border-[#BB162B]" : o === "Extend" ? "bg-[#D97706] text-white border-[#D97706]" : "bg-[#1B7F3B] text-white border-[#1B7F3B]"
                      : "border-gray-300 text-[#0E2240] hover:bg-gray-50"}`}>
                    {o}
                  </button>
                ))}
              </div>
              {rev.outcome === "Exit" && (
                <div className="text-[11px] text-[#BB162B] border border-[#BB162B]/30 bg-[#BB162B]/5 rounded-sm p-2.5">
                  Exit within probation is tagged as early attrition — back to the hiring source and interview panel, and reported monthly.
                </div>
              )}
              <textarea data-testid="review-comments" className={`${inputCls} min-h-[70px]`} placeholder="Comments"
                value={rev.comments} onChange={(e) => setRev((r) => ({ ...r, comments: e.target.value }))} />
            </div>
            <DialogFooter>
              <button onClick={() => setRevDlg(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Cancel</button>
              <button data-testid="review-confirm" disabled={busy} onClick={completeReview}
                className="px-4 py-2 text-sm bg-[#0E2240] text-white rounded-sm hover:bg-[#1a3358] disabled:opacity-50">Record review</button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
